import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { History, Clock, FileText } from "lucide-react";

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return dateStr; }
};

const getStatusBadge = (status) => {
  const variants = {
    success: <Badge className="bg-green-100 text-green-800">Success</Badge>,
    partial: <Badge className="bg-amber-100 text-amber-800">Partial</Badge>,
    failed: <Badge className="bg-red-100 text-red-800">Failed</Badge>,
  };
  return variants[status] || <Badge variant="outline">{status}</Badge>;
};

export const ImportHistory = ({ importHistory, loadingHistory }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="w-5 h-5" />
          Import History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
            <span className="ml-2 text-slate-500">Loading history...</span>
          </div>
        ) : importHistory.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No import history yet. Upload your first file to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="import-history-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 font-medium text-slate-600">Date</th>
                  <th className="text-left py-3 px-2 font-medium text-slate-600">Filename</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Records</th>
                  <th className="text-right py-3 px-2 font-medium text-slate-600">Amount</th>
                  <th className="text-right py-3 px-2 font-medium text-slate-600">TDS</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((record) => (
                  <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-700">{formatDate(record.created_at)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-slate-900 font-medium">{record.filename || 'Unknown'}</span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="text-green-700">{record.inserted}</span>
                      <span className="text-slate-400">/{record.total_records}</span>
                      {record.errors > 0 && (
                        <span className="text-red-500 ml-1">({record.errors} errors)</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-slate-700">
                      {formatCurrency(record.total_amount)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-slate-700">
                      {formatCurrency(record.total_tds)}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {getStatusBadge(record.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
